/**
 * Decides which anchor-position axis (X, Y, both, or neither) a slider's chosen `aspectRatio`
 * actually gives the author control over, matching inspectorOptions.tsx's media Anchor Position
 * gating — but always assuming a landscape source image (there's no orientation control for the
 * slider), so this is only the `defaultImageOrientation === 'landscape'` branch of that logic.
 *
 * A crop only moves along an axis when the target aspect ratio differs from the image's native
 * ratio on that axis: a square target can only slide horizontally over a landscape source, while
 * a wider-than-native target can only slide vertically. When the target ratio matches the native
 * ratio (within a small tolerance), nothing crops and neither axis does anything.
 */
export function getSliderAnchorVisibility(args: { aspectRatio: string; width?: number; height?: number }): {
  hasXAnchor: boolean
  hasYAnchor: boolean
} {
  const { aspectRatio } = args
  const width = args.width || 1
  const height = args.height || 1

  if (aspectRatio === 'unset') {
    return { hasXAnchor: false, hasYAnchor: false }
  }

  if (aspectRatio === '1/1') {
    return { hasXAnchor: true, hasYAnchor: false }
  }

  const nativeAspectRatio = Math.max(width, height) / Math.min(width, height)
  const [ratioWidth, ratioHeight] = aspectRatio.split('/')
  const parsedAspectRatio = Number.parseInt(ratioWidth, 10) / Number.parseInt(ratioHeight, 10)
  const isNativeAspectRatio = Math.abs(nativeAspectRatio - parsedAspectRatio) < 0.02

  if (isNativeAspectRatio) {
    return { hasXAnchor: false, hasYAnchor: false }
  }

  if (parsedAspectRatio < nativeAspectRatio) {
    return { hasXAnchor: true, hasYAnchor: false }
  }

  return { hasXAnchor: false, hasYAnchor: true }
}
